import { ClientEvents } from 'common/events/ClientEvents';
import { StorageKeys } from 'common/storage/constants';
import { QueryParams } from 'common/urls/QueryParams';
import { Slugs } from 'common/urls/Slugs';
import QueryString from 'query-string';
import { useContext, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { useMealPlanSurfacingLogic } from 'src/pages/consumer/meal-plans/hooks/useMealPlanSurfacingLogic';
import { useMarketSegmentContent } from 'src/rebrand/Homepage/useMarketSegmentContent';
import { useLocation } from 'src/shared/hooks/useLocation';
import { marketSegmentFromParams, useMarketSegment } from 'src/shared/hooks/useMarketSegment';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useUtm } from 'src/shared/hooks/useUtm';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { StringParam, useQueryParam } from 'use-query-params';
// 10/7/24 when we deprecate rebrand/homepage, pull this logic over
import { useHomepageRedirects } from '../../../rebrand/Homepage/useHomepageRedirects';
import { ZipCodeSelectionModalContext } from '../components/header-v2/ZipCodeSelection/ZipCodeSelectionModalContext';
import { HomepageHeroSection } from './components/HomepageHeroSection';
import { MadeNotManufactured } from './components/MadeNotManufactured';
import { ProductStepsSection } from './components/ProductStepsSection';
import { ReviewsSection } from './components/ReviewsSection';
import { ShefCarousel } from './components/ShefCarousel';
import { TopValueCalloutSection } from './components/TopValueCalloutSection';

const Homepage = () => {
  const location = useLocation();
  const history = useHistory();
  const utmParams = useUtm({ location });
  const tracker = useTracker();
  const [community] = useQueryParam(QueryParams.COMMUNITY, StringParam);
  const { mealPlanIntakeRouteByRegion } = useMealPlanSurfacingLogic();
  const { showZipCodeModal } = useContext(ZipCodeSelectionModalContext);
  const [, setVisitedExplorePage] = useLocalStorage<boolean>(StorageKeys.VISITED_EXPLORE_PAGE, false);
  const [, setVisitedMealPlans] = useLocalStorage<boolean>(StorageKeys.VISITED_MEAL_PLANS, false);
  const utmCampaign = utmParams?.campaign;
  const utmTerm = utmParams?.term;
  const { setMarketSegment, marketSegment } = useMarketSegment();
  const { exploreQueryParams } = useMarketSegmentContent(marketSegment, false);
  useHomepageRedirects({ exploreQueryParams });
  useEffect(
    () => setMarketSegment(marketSegmentFromParams({ utmCampaign, utmTerm, community })),
    [utmCampaign, utmTerm, setMarketSegment, community]
  );
  const [cachedZipCode, , zipCodeMetaData] = useCachedZipCode();
  const regionId = useMemo(() => zipCodeMetaData?.cachedZipCodeData?.zipCode?.region?.id ?? 0, [zipCodeMetaData]);
  const redirectToExplore = () => {
    const { availableToCustomers } = zipCodeMetaData?.cachedZipCodeData?.zipCode ?? {};

    const route = !availableToCustomers
      ? Routes.WAITLIST_HOME
      : getSluggedPath(
          Routes.CONSUMER_EXPLORE,
          [
            {
              slug: Slugs.ZIP_CODE,
              value: cachedZipCode ?? '',
            },
          ],
          { ...QueryString.parse(history.location.search) }
        );
    history.push(route);
  };

  const exploreButtonOnClick = () => {
    tracker.track(ClientEvents.HOMEPAGE_MARKETPLACE_CLICK, {
      placement: 'hero',
    });
    if (!cachedZipCode) {
      // 10/26/24 Zip modal redirects, so set visitedExplorePage to avoid a mealPlanUrl from mealPlanSurfacingLogic
      setVisitedExplorePage(true);
      showZipCodeModal({ isMealPlan: false, showCloseButton: true });
      return;
    }

    redirectToExplore();
  };

  const mealPlanButtonOnClick = (placement?: string) => {
    tracker.track(ClientEvents.HOMEPAGE_MEAL_PLAN_CLICK, {
      placement: placement ?? 'hero',
    });
    if (!regionId) {
      setVisitedMealPlans(true);
      showZipCodeModal({
        isMealPlan: true,
        redirectOnSubmit: false,
        showCloseButton: true,
      });
      return;
    }

    if (mealPlanIntakeRouteByRegion) {
      history.push(mealPlanIntakeRouteByRegion);
      return;
    }

    redirectToExplore();
  };

  return (
    <>
      <Helmet>
        <link rel='canonical' href='https://shef.com' />
      </Helmet>
      <HomepageHeroSection
        mealPlanButtonOnClick={() => mealPlanButtonOnClick('hero')}
        exploreButtonOnClick={exploreButtonOnClick}
      />
      <ProductStepsSection />
      <ReviewsSection />
      <TopValueCalloutSection mealPlanButtonOnClick={() => mealPlanButtonOnClick('meal-plan-info')} />
      <ShefCarousel />
      <MadeNotManufactured />
    </>
  );
};

export default Homepage;
